// src/ViewOrder.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import { useTheme } from "./contexts/ThemeContext";
import logo from "./assets/logo.png";

import { useCart } from "./contexts/CartContext";

export default function ViewOrder() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const { itemCount } = useCart();
  const { darkMode, setDarkMode } = useTheme();

  useEffect(() => {
    async function fetchOrder() {
      try {
        const q = query(
          collection(db, "orders"),
          where("orderNumber", "==", orderNumber)
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          setOrder(snapshot.docs[0].data());
        } else {
          setOrder(false);
        }
      } catch (err) {
        console.error("Error loading order:", err);
        setOrder(false);
      }
      setLoading(false);
    }

    fetchOrder();
  }, [orderNumber]);

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className="min-h-screen bg-gradient-to-b from-pink-100 to-white dark:from-gray-900 dark:to-gray-800 text-gray-800 dark:text-gray-200 font-sans">

        {/* HEADER */}
        <header className="w-full h-16 md:h-20 shadow-sm bg-white/80 dark:bg-gray-900/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-6xl mx-auto flex items-center justify-between h-full px-4 gap-4">
            {/* Mobile Home Button */}
            <button
              onClick={() => navigate("/")}
              className="md:hidden p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition"
            >
              <i className="fa-solid fa-house text-xl" />
            </button>

            {/* LOGO */}
            <div onClick={() => navigate("/")} className="flex items-center gap-3 cursor-pointer">
              <img src={logo} alt="Bubble Butter Logo" className="h-14 md:h-16 object-contain" />
              <h1 className="text-xl md:text-2xl font-bold text-pink-600 dark:text-pink-300">
                Bubble Butter
              </h1>
            </div>

            {/* DESKTOP NAV */}
            <div className="hidden md:flex items-center gap-4">
              <button onClick={() => navigate("/")} className="hover:text-pink-600 dark:hover:text-pink-300">Home</button>
              <button onClick={() => navigate("/products")} className="hover:text-pink-600 dark:hover:text-pink-300">Products</button>
              <button
                onClick={() => navigate("/cart")}
                className="relative px-3 py-2 rounded-lg bg-pink-600 text-white shadow"
              >
                🛒
              </button>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition shadow-sm"
              >
                {darkMode ? "🌙" : "☀️"}
              </button>
            </div>

            {/* MOBILE CART */}
            <div className="md:hidden flex items-center gap-3">
              <button
                onClick={() => navigate("/cart")}
                className="relative p-2 rounded-lg bg-pink-600 text-white shadow"
              >
                🛒
              </button>
            </div>
          </div>
        </header>

        {/* CONTENT */}
        <div className="max-w-3xl mx-auto p-6 pt-10">
          <h2 className="text-3xl font-bold text-center text-pink-600 dark:text-pink-300 mb-6">
            View Order
          </h2>

          {loading && <p className="text-center text-lg">Loading order details…</p>}

          {!loading && order === false && (
            <div className="text-center p-6 bg-white dark:bg-gray-800 shadow rounded-xl">
              <p className="text-xl text-red-500 font-semibold mb-3">Order Not Found</p>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                No order exists with ID: <b>{orderNumber}</b>
              </p>
              <button
                onClick={() => navigate("/")}
                className="px-4 py-2 bg-pink-600 text-white rounded-lg"
              >
                Go Home
              </button>
            </div>
          )}

          {!loading && order && (
            <div className="bg-white dark:bg-gray-800 shadow-lg rounded-2xl p-6 space-y-6">

              {/* Order Number */}
              <div className="text-center">
                <p className="text-lg text-gray-600 dark:text-gray-300">Order Number:</p>
                <p className="text-2xl font-mono font-bold text-pink-600 dark:text-pink-300">
                  {order.orderNumber}
                </p>
              </div>

              {/* Customer Details */}
              <div>
                <h3 className="text-xl font-bold text-pink-500 mb-2">Customer Details</h3>
                <div className="space-y-1 text-gray-700 dark:text-gray-300">
                  <p><b>Name:</b> {order.customer?.name}</p>
                  <p><b>Email:</b> {order.customer?.email}</p>
                  <p><b>Mobile:</b> {order.customer?.mobile}</p>
                  <p><b>Address:</b> {order.customer?.address}</p>
                </div>
              </div>

              {/* Items */}
              <div>
                <h3 className="text-xl font-bold text-pink-500 mb-2">Items</h3>
                <div className="space-y-3">
                  {order.items?.map((item, idx) => (
                    <div
                      key={idx}
                      className="p-3 rounded-lg border bg-pink-50 dark:bg-gray-900 dark:border-gray-700"
                    >
                      <p className="font-semibold">{item.name}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Qty: {item.qty} × ₹{item.price}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="text-right text-lg font-bold text-pink-600 dark:text-pink-300 space-y-1">
                <div>Subtotal: ₹{order.total}</div>
                <div>Shipping: ₹{order.shippingCharge || 0}</div>
                <div>Grand Total: ₹{order.grandTotal || order.total}</div>
              </div>

              {/* Shipment Tracking */}
              {order.shipment && (
                <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg space-y-2">
                  <h3 className="text-lg font-semibold text-pink-500">Shipment Tracking</h3>
                  <p><b>Courier:</b> {order.shipment.courierName}</p>
                  <p><b>Waybill:</b> {order.shipment.waybill}</p>
                  {order.shipment.trackingUrl && (
                    <a
                      href={order.shipment.trackingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-pink-600 underline"
                    >
                      Track Shipment
                    </a>
                  )}
                </div>
              )}

              {/* Continue Shopping */}
              <button
                onClick={() => navigate("/products")}
                className="w-full py-3 rounded-xl bg-pink-600 hover:bg-pink-700 text-white shadow-lg transition"
              >
                Continue Shopping
              </button>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
